import {
  Component,
  Input,
  signal,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { SettingsService } from '../settings.service';
import { GetSystemConfigViewModel } from 'edu-application/dist/system-config/get-system-config/get-system-config.veiwmodel';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PermissionsService } from '../../shared/permissions/permissions.service';
import { PERMISSIONS } from 'edu-core';
import { OrganizationService } from 'src/app/shared/organization.service';
import { environment } from 'src/environments/environment';
import { CORRECTOR_KINDS, CORRECTOR_PROVIDERS } from 'edu-core';
import type {
  CorrectorKind,
  CorrectorProviders,
} from 'edu-core/dist/data/corrector';
import type { LlmProvider } from 'edu-core/dist/entities/system-config/system-config';

/** The value a row carries while no explicit provider is chosen. */
const USE_DEFAULT = '';

interface CorrectorRow {
  kind: CorrectorKind;
  name: string;
  description: string;
  providers: readonly LlmProvider[];
  /** Correctors with a single option cannot be changed, only explained. */
  fixed: boolean;
}

const CORRECTOR_LABELS: Record<CorrectorKind, { name: string; description: string }> = {
  standard: {
    name: 'Standard Mode',
    description: 'Marks an answer against its marking scheme.',
  },
  agent: {
    name: 'Agent Mode',
    description: 'Reasons over the question criteria before marking.',
  },
  checklist: {
    name: 'Checklist Analysis',
    description: 'Scores a submission against a checklist.',
  },
  coding: {
    name: 'Coding Corrector',
    description: 'Marks code submissions.',
  },
  'ict-corrector': {
    name: 'Practical ICT Corrector',
    description: 'Marks practical ICT tasks.',
  },
  english: {
    name: 'English Corrector',
    description: 'Marks English writing submissions.',
  },
  'arabic-essay': {
    name: 'Arabic Essays Corrector',
    description: 'Marks Arabic Paper 1 (Writing) essays against the examiner rubric.',
  },
  'english-essay': {
    name: 'English Essays Corrector',
    description:
      'Marks IGCSE ESL Paper 2 (Writing) against the examiner rubric.',
  },
};

const PROVIDER_LABELS: Record<LlmProvider, string> = {
  claude: 'Claude',
  openai: 'OpenAI',
  gemini: 'Gemini',
  kimi: 'Kimi',
  deepseek: 'DeepSeek',
};

@Component({
  selector: 'edu-ai-settings',
  templateUrl: './ai-settings.component.html',
  styleUrl: './ai-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AiSettingsComponent {
  @Input() orgId!: string;
  @Input() systemConfig: GetSystemConfigViewModel | null = null;

  /** Only correctors the head has made a choice for appear here. */
  selections = signal<CorrectorProviders>({});
  isSaving = signal(false);

  readonly useDefault = USE_DEFAULT;
  readonly correctors: CorrectorRow[];

  constructor(
    private settingsService: SettingsService,
    private snackBar: MatSnackBar,
    private permissions: PermissionsService,
    private organizationService: OrganizationService,
  ) {
    this.correctors = CORRECTOR_KINDS.filter((kind) =>
      this.isAvailable(kind),
    ).map((kind) => ({
      kind,
      ...CORRECTOR_LABELS[kind],
      providers: CORRECTOR_PROVIDERS[kind],
      fixed: CORRECTOR_PROVIDERS[kind].length === 1,
    }));
  }

  /**
   * Correctors this organization can actually put to work.
   *
   * `coding` and `ict-corrector` are restricted to the organizations that own
   * them — the same rule EditQuestionUsecase enforces when a question picks a
   * corrector — so offering the rest of the estate a provider choice for a
   * corrector they can never select would only be a dead setting.
   */
  private isAvailable(kind: CorrectorKind): boolean {
    const currentOrg = this.organizationService.getOrganizationId();
    switch (kind) {
      case 'coding':
        return (
          !!environment.csteamOrganizationId &&
          currentOrg === environment.csteamOrganizationId
        );
      case 'ict-corrector':
        return (
          !!environment.icteamOrganizationId &&
          currentOrg === environment.icteamOrganizationId
        );
      default:
        return true;
    }
  }

  /** Writing these settings requires organization-settings.manage. */
  get canManage(): boolean {
    return this.permissions.has(PERMISSIONS.organizationSettings.manage);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['systemConfig'] && this.systemConfig) {
      const stored = this.systemConfig.correctorProviders ?? {};
      // Saving sends the whole map, so anything hydrated here is re-persisted.
      // Dropping correctors this organization cannot use keeps what is stored
      // in step with what the screen actually shows.
      const shown: CorrectorProviders = {};
      for (const { kind } of this.correctors) {
        if (stored[kind]) shown[kind] = stored[kind];
      }
      this.selections.set(shown);
    }
  }

  providerLabel(provider: LlmProvider): string {
    return PROVIDER_LABELS[provider];
  }

  /**
   * "Default" is not always the corrector's historical provider: an
   * organization-wide provider set before this screen existed still takes
   * precedence over it, so name it rather than let the label imply otherwise.
   */
  get defaultLabel(): string {
    const organizationWide = this.systemConfig?.llmProvider;
    return organizationWide
      ? `Default (${this.providerLabel(organizationWide)})`
      : 'Default';
  }

  selectionFor(kind: CorrectorKind): string {
    return this.selections()[kind] ?? USE_DEFAULT;
  }

  select(kind: CorrectorKind, value: string): void {
    const next = { ...this.selections() };
    if (value === USE_DEFAULT) {
      delete next[kind];
    } else {
      next[kind] = value as LlmProvider;
    }
    this.selections.set(next);
  }

  save(): void {
    this.isSaving.set(true);
    this.settingsService
      .setCorrectorProviders(this.orgId, this.selections())
      .subscribe({
        next: () => {
          this.snackBar.open('✅ AI correctors saved successfully', 'Dismiss', {
            duration: 3000,
          });
          this.isSaving.set(false);
        },
        error: () => {
          this.snackBar.open('❌ Failed to save AI correctors', 'Dismiss', {
            duration: 3000,
          });
          this.isSaving.set(false);
        },
      });
  }
}
